// src/App.js
import React, { useState, useEffect, useMemo } from 'react';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Navbar, Nav, Row, Col } from 'react-bootstrap';

// API hook / instance
import { api, authApi } from './hooks/useApi';

// Utilities
import { toPerformanceArray, findNearestPoint, pickFlow, pickPressure, getShaftPower, interpolatePerformanceAtPoint } from './utils/performance';

// Components
import FanForm from './components/FanForm';
import FansTable from './components/FansTable';
import PerformanceCharts from './components/PerformanceCharts';
import PrintPreviewModal from './components/PrintPreviewModal';

// Chart setup (register components + plugin)
import './setupChart';

const INITIAL_FORM_VALUES = {
  airFlow: '',
  airFlowUnit: 'cfm',
  airFlowTolNeg: '5',
  airFlowTolPos: '100',
  staticPressure: '',
  staticPressureUnit: 'inchh2o',
  staticPressureTolNeg: '5',
  staticPressureTolPos: '100',
  frequency: '60',
  categorySelect: '',
  airTemperature: 20,
  airDensity: 1.204575182493151,
};

function extractAxiosErrorMessage(error) {
  if (error?.response?.data) {
    const data = error.response.data;
    if (data?.errors) {
      const messages = Object.values(data.errors).flat();
      if (messages.length > 0) {
        return messages[0];
      }
    }
    if (data?.message) {
      return data.message;
    }
  }

  if (error?.message) {
    return error.message;
  }

  return 'Unable to complete the request.';
}

/**
 * App - Main application
 */
export default function App() {
  // --- state ---
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const [formData, setFormData] = useState({ ...INITIAL_FORM_VALUES });

  const [categories, setCategories] = useState([]);
  const [fans, setFans] = useState([]);
  const [selectedFan, setSelectedFan] = useState(null);
  const [userPoint, setUserPoint] = useState(null);
  const [nearestPoint, setNearestPoint] = useState(null);
  const [hasMatches, setHasMatches] = useState(true);
  const [apiMessage, setApiMessage] = useState('');
  const [activeTab, setActiveTab] = useState('input');

  const [selectedItems, setSelectedItems] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // motor-related state
  const [motorsForCategory, setMotorsForCategory] = useState([]);
  const [selectedMotorForPrint, setSelectedMotorForPrint] = useState(null);
  const [certificates, setCertificates] = useState([]);

  // loading state for main search
  const [loading, setLoading] = useState(false);

  // Active units for chart/PDF display (only updated on search)
  const [activeAirFlowUnit, setActiveAirFlowUnit] = useState('m3h');
  const [activeStaticPressureUnit, setActiveStaticPressureUnit] = useState('pa');

  // --- helpers ---
  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');
    setLoggingIn(true);

    try {
      const response = await authApi.login(loginForm.email, loginForm.password);
      if (response?.user) {
        setUser(response.user);
        setLoginForm({ email: '', password: '' });
      } else {
        setLoginError('Unable to sign in. Please try again.');
      }
    } catch (err) {
      setLoginError(extractAxiosErrorMessage(err));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error', err);
    } finally {
      setUser(null);
      setFormData({ ...INITIAL_FORM_VALUES });
      setCategories([]);
      setFans([]);
      setSelectedFan(null);
      setUserPoint(null);
      setNearestPoint(null);
      setSelectedItems([]);
      setShowPrintModal(false);
      // Reset active units
      setActiveAirFlowUnit('m3h');
      setActiveStaticPressureUnit('pa');
    }
  };

  const toNumberSafe = (v, fallback = null) => {
    if (v === null || v === undefined || v === '') return fallback;
    if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
    const n = Number(String(v).trim());
    return Number.isFinite(n) ? n : fallback;
  };

  // Convert airflow from selected unit to m³/h (database unit)
  const convertAirflowToM3H = (value, unit) => {
    const numValue = toNumberSafe(value, null);
    if (numValue === null) return null;

    switch (unit) {
      case 'm3h':
        return numValue; // already in m³/h
      case 'm3s':
        return numValue * 3600; // m³/s to m³/h: multiply by 3600
      case 'ls':
        return numValue * 3.6; // l/s to m³/h: multiply by 3.6 (1 l/s = 0.001 m³/s = 3.6 m³/h)
      case 'cfm':
        return numValue * 1.69901079552; // cfm to m³/h: multiply by 1.69901079552
      default:
        return numValue; // fallback to no conversion
    }
  };

  // Convert pressure from selected unit to Pa (database unit)
  const convertPressureToPa = (value, unit) => {
    const numValue = toNumberSafe(value, null);
    if (numValue === null) return null;

    switch (unit) {
      case 'pa':
        return numValue; // already in Pa
      case 'mmh2o':
        return numValue * 9.80665; // mm H₂O to Pa: multiply by 9.80665
      case 'inchh2o':
        return numValue * 249.08891; // inch H₂O to Pa: multiply by 249.08891
      case 'mbar':
        return numValue * 100; // mbar to Pa: multiply by 100
      case 'kpa':
        return numValue * 1000; // kPa to Pa: multiply by 1000
      default:
        return numValue; // fallback to no conversion
    }
  };

  // Convert airflow from m³/h (database unit) to selected unit
  const convertAirflowFromM3H = (value, unit) => {
    const numValue = toNumberSafe(value, null);
    if (numValue === null) return null;

    switch (unit) {
      case 'm3h':
        return numValue; // already in m³/h
      case 'm3s':
        return numValue / 3600; // m³/h to m³/s: divide by 3600
      case 'ls':
        return numValue / 3.6; // m³/h to l/s: divide by 3.6
      case 'cfm':
        return numValue / 1.69901079552; // m³/h to cfm: divide by 1.69901079552
      default:
        return numValue; // fallback to no conversion
    }
  };

  // Convert pressure from Pa (database unit) to selected unit
  const convertPressureFromPa = (value, unit) => {
    const numValue = toNumberSafe(value, null);
    if (numValue === null) return null;

    switch (unit) {
      case 'pa':
        return numValue; // already in Pa
      case 'mmh2o':
        return numValue / 9.80665; // Pa to mm H₂O: divide by 9.80665
      case 'inchh2o':
        return numValue / 249.08891; // Pa to inch H₂O: divide by 249.08891
      case 'mbar':
        return numValue / 100; // Pa to mbar: divide by 100
      case 'kpa':
        return numValue / 1000; // Pa to kPa: divide by 1000
      default:
        return numValue; // fallback to no conversion
    }
  };

  // Helper to get airflow unit label
  const getAirflowUnitLabel = (unit) => {
    switch (unit) {
      case 'm3h': return 'm³/h';
      case 'm3s': return 'm³/s';
      case 'ls': return 'l/s';
      case 'cfm': return 'cfm';
      default: return 'm³/h';
    }
  };

  // Helper to get pressure unit label
  const getPressureUnitLabel = (unit) => {
    switch (unit) {
      case 'pa': return 'Pa';
      case 'mmh2o': return 'mm H₂O';
      case 'inchh2o': return 'inch H₂O';
      case 'mbar': return 'mbar';
      case 'kpa': return 'kPa';
      default: return 'Pa';
    }
  };

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        const response = await authApi.check();
        if (!mounted) return;
        if (response?.authenticated && response.user) {
          setUser(response.user);
        } else {
          setUser(null);
        }
      } catch (err) {
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setCheckingAuth(false);
        }
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // --- lifecycle: load categories ---
  useEffect(() => {
    if (!user) {
      setCategories([]);
      return;
    }

    let mounted = true;
    api
      .get('/api/categories')
      .then((response) => {
        if (!mounted) return;
        setCategories(response?.data?.categories ?? []);
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          setUser(null);
          return;
        }
        console.error('Error fetching categories', error);
      });
    return () => { mounted = false; };
  }, [user]);

  // --- API helpers ---
  const fetchFanPerformance = async (fanId) => {
    try {
      const res = await api.get(`/api/fans/${fanId}/performance`);
      return toPerformanceArray(res?.data?.performance_data ?? res?.data ?? []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setUser(null);
        return [];
      }
      console.error('Error fetching performance data for fan', fanId, err);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      const categoryIds = (selectedItems && selectedItems.length > 0)
        ? selectedItems.map((it) => Number(it.id)).filter((n) => !Number.isNaN(n))
        : [];

      // Convert airflow to m³/h and pressure to Pa before sending to API (but keep original in formData)
      const airFlowM3H = convertAirflowToM3H(formData.airFlow, formData.airFlowUnit || 'm3h');
      const staticPressurePa = convertPressureToPa(formData.staticPressure, formData.staticPressureUnit || 'pa');

      const payload = {
        ...formData,
        airFlow: airFlowM3H, // Always send in m³/h to API
        staticPressure: staticPressurePa, // Always send in Pa to API
        fan_category_id: categoryIds.length === 0 ? null : (categoryIds.length === 1 ? categoryIds[0] : categoryIds),
        categorySelect: categoryIds.length > 0 ? categoryIds.join(',') : formData.categorySelect,
      };

      const response = await api.post('/api/fan-selection', payload);
      const fansResult = response?.data?.fans ?? [];
      setFans(fansResult);
      setHasMatches(Boolean(response?.data?.hasMatches ?? fansResult.length > 0));
      setApiMessage(response?.data?.message ?? '');

      api.post('/api/analytics/search', {
        airflow: airFlowM3H,
        static_pressure: staticPressurePa,
        temperature: formData.airTemperature,
        density: formData.airDensity,
        has_matches: Boolean(response?.data?.hasMatches ?? fansResult.length > 0),
        results_count: fansResult.length,
        fan_ids: fansResult.slice(0, 5).map((fan) => fan.id).filter(Boolean),
        category_ids: categoryIds,
      }).catch(() => {});

      // Use converted values for user point (always in m³/h and Pa for calculations)
      const inputAirFlow = airFlowM3H;
      const inputStatic = staticPressurePa;
      const inputPoint = {
        airFlow: inputAirFlow,
        staticPressure: inputStatic,
      };
      setUserPoint(inputPoint);

      // Update active units for chart/PDF display (only when search is performed)
      setActiveAirFlowUnit(formData.airFlowUnit || 'm3h');
      setActiveStaticPressureUnit(formData.staticPressureUnit || 'pa');

      if (fansResult.length > 0) {
        // Calculate intersection points for all fans
        const fansWithIntersection = await Promise.all(fansResult.map(async (fan) => {
          // Fetch performance data for this fan
          const perf = await fetchFanPerformance(fan.id);
          const fanWithPerf = { ...fan, performance_data: perf };
          
          // Calculate intersection point in database units (m³/h and Pa)
          const intersectionDb = calculateIntersectionPoint(perf, inputPoint);
          
          if (intersectionDb) {
            // Interpolate performance data at the intersection point to get shaft power
            const interpolated = interpolatePerformanceAtPoint(
              perf,
              intersectionDb.flowM3H,
              intersectionDb.pressurePa
            );
            
            // Store intersection point in database units
            fanWithPerf.intersectionPoint = {
              airFlow: intersectionDb.flowM3H,
              airflow_m3h: intersectionDb.flowM3H,
              staticPressure: intersectionDb.pressurePa,
              static_pressure_pa: intersectionDb.pressurePa,
              shaftPower: interpolated?.shaftPower ?? interpolated?.shaft_power ?? interpolated?.shaft_power_kw ?? null,
              shaft_power_kw: interpolated?.shaftPower ?? interpolated?.shaft_power ?? interpolated?.shaft_power_kw ?? null,
            };
          }
          
          return fanWithPerf;
        }));
        
        setFans(fansWithIntersection);
        
        // Select first fan
        const first = fansWithIntersection[0];
        setSelectedFan(first);
        if (inputAirFlow !== null && inputStatic !== null) {
          const nearest = findNearestPoint(first.performance_data, inputPoint);
          setNearestPoint(nearest ?? null);
        } else {
          setNearestPoint(null);
        }
      } else {
        setSelectedFan(null);
        setNearestPoint(null);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        setUser(null);
        return;
      }
      console.error('Error fetching fan data', error);
      setHasMatches(false);
      setApiMessage('Unable to fetch results');
      setFans([]);
      setSelectedFan(null);
      setNearestPoint(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFanSelection = async (fan, openPrint = false) => {
    if (!fan) return;
    if (fan.performance_data && Array.isArray(fan.performance_data)) {
      // Ensure intersection point is calculated if not already present
      let fanToSelect = fan;
      if (userPoint && !fan.intersectionPoint) {
        const intersectionDb = calculateIntersectionPoint(fan.performance_data, userPoint);
        if (intersectionDb) {
          const interpolated = interpolatePerformanceAtPoint(
            fan.performance_data,
            intersectionDb.flowM3H,
            intersectionDb.pressurePa
          );
          fanToSelect = {
            ...fan,
            intersectionPoint: {
              airFlow: intersectionDb.flowM3H,
              airflow_m3h: intersectionDb.flowM3H,
              staticPressure: intersectionDb.pressurePa,
              static_pressure_pa: intersectionDb.pressurePa,
              shaftPower: interpolated?.shaftPower ?? interpolated?.shaft_power ?? interpolated?.shaft_power_kw ?? null,
              shaft_power_kw: interpolated?.shaftPower ?? interpolated?.shaft_power ?? interpolated?.shaft_power_kw ?? null,
            },
          };
          // Update in fans list as well
          setFans((prev) => prev.map((f) => (f.id === fan.id ? fanToSelect : f)));
        }
      }
      setSelectedFan(fanToSelect);
      const nearest = userPoint ? findNearestPoint(fan.performance_data, userPoint) : null;
      setNearestPoint(nearest ?? null);
      if (openPrint) setShowPrintModal(true);
      return;
    }

    const perfData = await fetchFanPerformance(fan.id);
    let fanWithPerf = { ...fan, performance_data: perfData };
    
    // Calculate intersection point if userPoint exists
    if (userPoint) {
      const intersectionDb = calculateIntersectionPoint(perfData, userPoint);
      if (intersectionDb) {
        const interpolated = interpolatePerformanceAtPoint(
          perfData,
          intersectionDb.flowM3H,
          intersectionDb.pressurePa
        );
        fanWithPerf = {
          ...fanWithPerf,
          intersectionPoint: {
            airFlow: intersectionDb.flowM3H,
            airflow_m3h: intersectionDb.flowM3H,
            staticPressure: intersectionDb.pressurePa,
            static_pressure_pa: intersectionDb.pressurePa,
            shaftPower: interpolated?.shaftPower ?? interpolated?.shaft_power ?? interpolated?.shaft_power_kw ?? null,
            shaft_power_kw: interpolated?.shaftPower ?? interpolated?.shaft_power ?? interpolated?.shaft_power_kw ?? null,
          },
        };
      }
    }
    
    setSelectedFan(fanWithPerf);
    setFans((prev) => prev.map((f) => (f.id === fan.id ? fanWithPerf : f)));

    if (userPoint) {
      const nearest = findNearestPoint(perfData, userPoint);
      setNearestPoint(nearest ?? null);
    } else {
      setNearestPoint(null);
    }

    if (openPrint) setShowPrintModal(true);
  };

  // Motor fetching strategy (tries multiple endpoints)
  const fetchMotorsForCategory = async (categoryId) => {
    if (!categoryId) return [];
    // try various shapes and fallbacks; return an array
    try {
      const res1 = await api.get(`/api/fan-categories/${categoryId}/motors`);
      if (res1?.data) {
        if (Array.isArray(res1.data)) return res1.data;
        if (Array.isArray(res1.data.motors)) return res1.data.motors;
        // if the payload is an object with keys
        return Array.isArray(res1.data) ? res1.data : (res1.data.motors ?? []);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setUser(null);
        return [];
      }
      // ignore, try next
    }

    try {
      const res2 = await api.get('/api/motors', { params: { fan_category_id: categoryId } });
      if (res2?.data) {
        if (Array.isArray(res2.data)) return res2.data;
        if (Array.isArray(res2.data.motors)) return res2.data.motors;
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setUser(null);
        return [];
      }
      // ignore, try fallback
    }

    try {
      const res3 = await api.get('/api/motors');
      const all = res3?.data?.motors ?? (Array.isArray(res3?.data) ? res3.data : []);
      if (!Array.isArray(all)) return [];
      const filtered = all.filter((m) => {
        if (!m) return false;
        if (Array.isArray(m.fan_category_ids) && m.fan_category_ids.map(Number).includes(Number(categoryId))) return true;
        if (Array.isArray(m.fan_categories) && m.fan_categories.some((c) => Number(c.id) === Number(categoryId))) return true;
        return false;
      });
      return filtered.length > 0 ? filtered : all;
    } catch (err) {
      if (err?.response?.status === 401) {
        setUser(null);
        return [];
      }
      console.error('fetchMotorsForCategory error', err);
      return [];
    }
  };

  const chooseMotorForFan = (motors, maxShaftPower) => {
    if (!Array.isArray(motors) || motors.length === 0) return null;
    const candidates = motors
      .map((m) => {
        const nominal =
          m?.nominal_motor_power ??
          m?.nominalMotorPower ??
          m?.power_kw ??
          m?.power ??
          m?.nominal_power_kw ??
          m?.kW ??
          null;
        const nominalN = toNumberSafe(nominal, NaN);
        return { motor: m, nominal: nominalN };
      })
      .filter((x) => Number.isFinite(x.nominal) && x.nominal > Number(maxShaftPower)); // must be greater than shaft power

    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.nominal - b.nominal);
    return candidates[0].motor;
  };

  // --- Chart data (memoized) ---
  const performanceData = useMemo(() => {
    if (!selectedFan) return [];
    // normalize toPerformanceArray so downstream code can rely on fields
    return toPerformanceArray(selectedFan.performance_data ?? selectedFan.performancePoints ?? selectedFan.performance_points ?? []);
  }, [selectedFan]);

  const sortedPerformanceData = useMemo(() => {
    return [...performanceData].sort((a, b) => {
      const aFlow = pickFlow(a);
      const bFlow = pickFlow(b);
      return aFlow - bFlow;
    });
  }, [performanceData]);

  // Helper function to calculate intersection point for any fan's performance data
  // Returns intersection point in database units (m³/h and Pa)
  const calculateIntersectionPoint = (performanceData, userPointInput) => {
    if (!performanceData || !userPointInput) return null;
    
    const perfArray = toPerformanceArray(performanceData);
    if (perfArray.length < 2) return null;

    const userX_M3H = Number(userPointInput.airFlow);
    const userY_Pa = Number(userPointInput.staticPressure);
    
    if (toNumberSafe(userX_M3H) === null || toNumberSafe(userY_Pa) === null || userX_M3H <= 0) {
      return null;
    }

    // System curve: P = k * Q², where k = userY / (userX²)
    const k = userY_Pa / (userX_M3H * userX_M3H);

    // Build performance curve points in database units
    const perfPoints = perfArray.map((d) => {
      const flowM3H = pickFlow(d);
      const pressurePa = pickPressure(d);
      return {
        x_m3h: flowM3H,
        y_pa: pressurePa,
      };
    });

    // Sort by flow for proper segment checking
    perfPoints.sort((a, b) => a.x_m3h - b.x_m3h);

    // Find intersection by checking each segment of the performance curve
    for (let i = 0; i < perfPoints.length - 1; i++) {
      const p1 = perfPoints[i];
      const p2 = perfPoints[i + 1];

      // Skip if segment is invalid
      if (p1.x_m3h === p2.x_m3h) continue;

      // System curve pressure at p1: P_sys1 = k * p1.x²
      // System curve pressure at p2: P_sys2 = k * p2.x²
      const pSys1 = k * p1.x_m3h * p1.x_m3h;
      const pSys2 = k * p2.x_m3h * p2.x_m3h;

      // Performance curve pressure at p1 and p2
      const pPerf1 = p1.y_pa;
      const pPerf2 = p2.y_pa;

      // Calculate difference at endpoints
      const diff1 = pSys1 - pPerf1;
      const diff2 = pSys2 - pPerf2;

      // If signs are opposite, there's a crossing (intersection)
      if (diff1 * diff2 <= 0) {
        // Use binary search to find exact intersection
        let lowX = p1.x_m3h;
        let highX = p2.x_m3h;
        let bestX = null;
        let bestDiff = Infinity;

        // Binary search for intersection point
        for (let iter = 0; iter < 50; iter++) {
          const midX = (lowX + highX) / 2;
          
          // System curve pressure at midX
          const pSysMid = k * midX * midX;
          
          // Performance curve pressure at midX (linear interpolation)
          const slope = (p2.y_pa - p1.y_pa) / (p2.x_m3h - p1.x_m3h);
          const pPerfMid = p1.y_pa + slope * (midX - p1.x_m3h);
          
          const diff = pSysMid - pPerfMid;

          if (Math.abs(diff) < bestDiff) {
            bestDiff = Math.abs(diff);
            bestX = midX;
          }

          if (Math.abs(diff) < 0.01) break; // Close enough

          if (diff > 0) {
            highX = midX;
          } else {
            lowX = midX;
          }
        }

        if (bestX !== null && bestX >= p1.x_m3h && bestX <= p2.x_m3h) {
          const pSysBest = k * bestX * bestX;
          // Return in database units (m³/h and Pa)
          return {
            flowM3H: bestX,
            pressurePa: pSysBest,
          };
        }
      }
    }

    // If no exact intersection found, find the closest point
    let closestPoint = null;
    let minDiff = Infinity;

    for (const point of perfPoints) {
      const pSys = k * point.x_m3h * point.x_m3h;
      const diff = Math.abs(pSys - point.y_pa);
      if (diff < minDiff) {
        minDiff = diff;
        closestPoint = {
          flowM3H: point.x_m3h,
          pressurePa: pSys,
        };
      }
    }

    return closestPoint;
  };

  // Find intersection point between performance curve and system curve
  // Note: Calculations are done in database units (m³/h and Pa), then converted to chart units
  const intersectionPoint = useMemo(() => {
    if (!userPoint || !sortedPerformanceData || sortedPerformanceData.length < 2) {
      return null;
    }

    const intersectionDb = calculateIntersectionPoint(sortedPerformanceData, userPoint);
    if (!intersectionDb) return null;

    // Convert to chart units (use active units)
    const airFlowUnit = activeAirFlowUnit || 'm3h';
    const pressureUnit = activeStaticPressureUnit || 'pa';
    return {
      x: convertAirflowFromM3H(intersectionDb.flowM3H, airFlowUnit) ?? 0,
      y: convertPressureFromPa(intersectionDb.pressurePa, pressureUnit) ?? 0,
    };
  }, [sortedPerformanceData, userPoint, activeAirFlowUnit, activeStaticPressureUnit]);

  // Create working point from intersection point (used for all calculations)
  // This is the actual working point where the system curve intersects the performance curve
  const workingPoint = useMemo(() => {
    if (!intersectionPoint || !sortedPerformanceData || sortedPerformanceData.length === 0) {
      return null;
    }

    // Convert intersection point from chart units back to database units (m³/h and Pa)
    const airFlowUnit = activeAirFlowUnit || 'm3h';
    const pressureUnit = activeStaticPressureUnit || 'pa';
    
    // Convert back to database units
    const intersectionFlowM3H = convertAirflowToM3H(intersectionPoint.x, airFlowUnit);
    const intersectionPressurePa = convertPressureToPa(intersectionPoint.y, pressureUnit);
    
    if (intersectionFlowM3H === null || intersectionPressurePa === null) {
      return null;
    }

    // Interpolate performance data at the intersection point
    const interpolated = interpolatePerformanceAtPoint(
      sortedPerformanceData,
      intersectionFlowM3H,
      intersectionPressurePa
    );

    return interpolated;
  }, [intersectionPoint, sortedPerformanceData, activeAirFlowUnit, activeStaticPressureUnit]);

  // When print modal opens, fetch motors and auto-choose
  useEffect(() => {
    if (!showPrintModal || !selectedFan) {
      if (!showPrintModal) {
        setMotorsForCategory([]);
        setSelectedMotorForPrint(null);
        setCertificates([]);
      }
      return;
    }

    (async () => {
      // Fetch motors assigned to this fan (not from category)
      let motors = [];
      try {
        const motorsRes = await api.get(`/api/fans/${selectedFan.id}/motors`);
        // API returns { motors: [...], suitable_motor: ... }
        const responseData = motorsRes?.data?.data ?? motorsRes?.data;
        motors = responseData?.motors ?? (Array.isArray(responseData) ? responseData : []);
      } catch (e) {
        if (e?.response?.status === 401) {
          // During print operations, don't log out on 401 - just continue without motors
          // This prevents logout when trying to print
          console.warn('Motors fetch failed with 401 during print, continuing without motors');
          motors = [];
        } else {
          console.error('fetchFanMotors error', e);
          motors = [];
        }
      }
      setMotorsForCategory(motors);

      const perfPoints = toPerformanceArray(selectedFan.performance_data ?? selectedFan.performance_points ?? selectedFan.performancePoints);
      let maxShaft = 0;
      perfPoints.forEach((p) => {
        const v = (p?.shaftPower ?? p?.shaft_power ?? p?.power ?? p?.shaft_power_kw ?? p?.shaftPowerKw ?? null);
        const vn = v !== null && v !== undefined ? Number(v) : null;
        if (vn !== null && !Number.isNaN(vn) && vn > maxShaft) maxShaft = vn;
      });

      // Also consider working point (intersection) shaft power for motor selection
      // This ensures the motor can handle the actual working point
      if (workingPoint) {
        const workingShaftPower = getShaftPower(workingPoint);
        if (workingShaftPower !== null && workingShaftPower !== undefined) {
          const workingShaftN = Number(workingShaftPower);
          if (!Number.isNaN(workingShaftN) && workingShaftN > maxShaft) {
            maxShaft = workingShaftN;
          }
        }
      }

      const chosen = chooseMotorForFan(motors, maxShaft);
      setSelectedMotorForPrint(chosen || null);

      // Fetch certificates for this fan
      try {
        const certRes = await api.get(`/api/fans/${selectedFan.id}/certificates`);
        const certs = certRes?.data?.data ?? certRes?.data ?? [];
        setCertificates(Array.isArray(certs) ? certs : []);
      } catch (e) {
        if (e?.response?.status === 401) {
          // If it's a 401, check if it's a real authentication issue or just a permission issue
          // For certificates, we'll just silently fail and not log the user out
          // Only log out if it's a critical authentication failure
          setCertificates([]);
          // Don't log out on certificate fetch failure - it might just be a permission issue
        } else {
          // For other errors, just set empty certificates
          setCertificates([]);
        }
      }
    })();
  }, [showPrintModal, selectedFan, workingPoint]);

  const chartData = useMemo(() => {
    // Get active units (only updated on search)
    const airFlowUnit = activeAirFlowUnit || 'm3h';
    const pressureUnit = activeStaticPressureUnit || 'pa';
    
    return {
      datasets: [
        {
          label: 'Performance Curve',
          data: sortedPerformanceData.map((d) => {
            // Convert from m³/h and Pa to user's units
            const flowM3H = Number(d.airFlow ?? d.airflow_m3h ?? d.air_flow ?? 0);
            const pressurePa = Number(d.staticPressure ?? d.static_pressure ?? d.static_pressure_pa ?? 0);
            return {
              x: convertAirflowFromM3H(flowM3H, airFlowUnit) ?? 0,
              y: convertPressureFromPa(pressurePa, pressureUnit) ?? 0,
            };
          }),
          borderColor: 'rgba(0,0,0,0.4)',
          backgroundColor: 'rgba(0,0,0,0.1)',
          showLine: true,
          fill: false,
          pointRadius: 0,
          tension: 0.4,
        },
        {
          label: 'User Input',
          data: userPoint && toNumberSafe(userPoint.airFlow) !== null && toNumberSafe(userPoint.staticPressure) !== null
            ? [{
                x: convertAirflowFromM3H(Number(userPoint.airFlow), airFlowUnit) ?? 0,
                y: convertPressureFromPa(Number(userPoint.staticPressure), pressureUnit) ?? 0,
              }]
            : [],
          backgroundColor: 'red',
          borderColor: 'red',
          pointRadius: 6,
          type: 'scatter',
        },
        {
          label: 'System Curve',
          data: (() => {
            if (!userPoint || toNumberSafe(userPoint.airFlow) === null || toNumberSafe(userPoint.staticPressure) === null) {
              return [];
            }
            // userPoint is stored in database units (m³/h and Pa), convert to chart units
            const userX_M3H = Number(userPoint.airFlow);
            const userY_Pa = Number(userPoint.staticPressure);
            
            // Convert to chart units for display
            const userX = convertAirflowFromM3H(userX_M3H, airFlowUnit) ?? 0;
            const userY = convertPressureFromPa(userY_Pa, pressureUnit) ?? 0;
            
            // System curve starts from origin (0,0) and curves to intersection point
            // Fan system curve follows: P = k * Q² (parabolic relationship)
            // Calculate k from user point: k = userY / (userX²)
            const k = userX > 0 ? userY / (userX * userX) : 0;
            
            // Determine where the curve should stop
            let maxFlow = userX;
            
            // If intersection point exists and is at or beyond the user point, stop at intersection
            if (intersectionPoint && intersectionPoint.x > 0 && intersectionPoint.x >= userX) {
              maxFlow = intersectionPoint.x;
            } else {
              // If no intersection OR user point is above/beyond the intersection, stop at user point (red point)
              maxFlow = userX;
            }
            
            // Create multiple points for a smooth curved line from origin to maxFlow
            const points = [];
            const numPoints = 50; // Increased points for smoother curve
            
            for (let i = 0; i <= numPoints; i++) {
              const t = i / numPoints;
              const x = maxFlow * t;
              // Use parabolic relationship: y = k * x²
              const y = k * x * x;
              
              points.push({ x, y });
            }
            
            return points;
          })(),
          borderColor: 'rgba(59, 130, 246, 0.8)',
          backgroundColor: 'transparent',
          showLine: true,
          fill: false,
          pointRadius: 0,
          borderWidth: 2,
          borderDash: [],
          tension: 0.4, // Creates a smooth curved line (spline-like)
        },
        {
          label: 'Duty Point',
          data: intersectionPoint ? [{ x: intersectionPoint.x, y: intersectionPoint.y }] : [],
          backgroundColor: 'green',
          borderColor: 'green',
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 2,
          pointBorderColor: 'darkgreen',
          type: 'scatter',
        },
      ],
    };
  }, [sortedPerformanceData, userPoint, intersectionPoint, activeAirFlowUnit, activeStaticPressureUnit]);

  const powerChartData = useMemo(() => {
    // Get active airflow unit (only updated on search, power stays in kW)
    const airFlowUnit = activeAirFlowUnit || 'm3h';
    
    return {
      datasets: [
        {
          label: 'Shaft Power Curve',
          data: sortedPerformanceData
            .map((d) => {
              const flowM3H = Number(d.airFlow ?? d.airflow_m3h ?? 0);
              const power = Number(getShaftPower(d));
              return {
                x: convertAirflowFromM3H(flowM3H, airFlowUnit) ?? 0,
                y: power,
              };
            })
            .filter((p) => p.y !== null && p.y !== undefined && !Number.isNaN(p.y)),
          borderColor: 'rgba(0, 0, 0, 0.6)',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          showLine: true,
          fill: false,
          pointRadius: 0,
          tension: 0.3,
        },
        {
          label: 'User Input (Power)',
          data:
            userPoint && workingPoint && getShaftPower(workingPoint) !== null
              ? [{ 
                  x: convertAirflowFromM3H(Number(userPoint.airFlow), airFlowUnit) ?? 0, 
                  y: Number(getShaftPower(workingPoint)) 
                }]
              : [],
          backgroundColor: 'red',
          borderColor: 'red',
          pointRadius: 6,
          type: 'scatter',
        },
        {
          label: 'Working Point (Power)',
          data:
            workingPoint && getShaftPower(workingPoint) !== null
              ? [{
                  x: convertAirflowFromM3H(
                    Number(workingPoint.airFlow ?? workingPoint.airflow_m3h ?? 0),
                    airFlowUnit
                  ) ?? 0,
                  y: Number(getShaftPower(workingPoint)),
                }]
              : [],
          backgroundColor: 'green',
          borderColor: 'green',
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 2,
          pointBorderColor: 'darkgreen',
          type: 'scatter',
        },
        {
          label: 'System Curve',
          data: (() => {
            if (!userPoint || !workingPoint || getShaftPower(workingPoint) === null) {
              return [];
            }
            // Convert user point airflow to chart units
            const userX = convertAirflowFromM3H(Number(userPoint.airFlow), airFlowUnit) ?? 0;
            const userY = Number(getShaftPower(workingPoint));
            
            // System curve starts from origin (0,0) and curves to working point
            // Fan system curve follows: P = k * Q² (parabolic relationship)
            // Calculate k from user point: k = userY / (userX²)
            const k = userX > 0 ? userY / (userX * userX) : 0;
            
            // Determine where the curve should stop
            let maxFlow = userX;
            
            // If working point exists and is at or beyond the user point, stop at working point
            if (workingPoint) {
              const workingFlowM3H = Number(workingPoint.airFlow ?? workingPoint.airflow_m3h ?? 0);
              const workingFlowChart = convertAirflowFromM3H(workingFlowM3H, airFlowUnit) ?? userX;
              if (workingFlowChart > 0 && workingFlowChart >= userX) {
                maxFlow = workingFlowChart;
              } else {
                // If working point is before user point, stop at user point (red point is above curve)
                maxFlow = userX;
              }
            } else {
              // If no working point, stop at user point (red point is above curve)
              maxFlow = userX;
            }
            
            // Create multiple points for a smooth curved line from origin to maxFlow
            const points = [];
            const numPoints = 50; // Increased points for smoother curve
            
            for (let i = 0; i <= numPoints; i++) {
              const t = i / numPoints;
              const x = maxFlow * t;
              // Use parabolic relationship: y = k * x²
              const y = k * x * x;
              
              points.push({ x, y });
            }
            
            return points;
          })(),
          borderColor: 'rgba(59, 130, 246, 0.8)',
          backgroundColor: 'transparent',
          showLine: true,
          fill: false,
          pointRadius: 0,
          borderWidth: 2,
          borderDash: [],
          tension: 0.4, // Creates a smooth curved line (spline-like)
        },
      ],
    };
  }, [sortedPerformanceData, userPoint, workingPoint, activeAirFlowUnit]);

  // Chart click handler stub (unused but left for PerformanceCharts)
  const handleChartClick = (evt) => {
    // placeholder for pixel-to-scale translation if desired
  };

  if (checkingAuth) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        <div className="text-muted">Checking your session…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: '#f8f9fa', padding: '16px' }}>
        <div className="card shadow-sm" style={{ width: '100%', maxWidth: '380px', padding: '24px' }}>
          <div className="text-center mb-3">
            <img
              src={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/logo.png`}
              alt="APT | Air Purification Trading logo"
              style={{ height: '60px', objectFit: 'contain' }}
            />
          </div>
          <h2 style={{ fontSize: '20px', textAlign: 'center', marginBottom: '16px' }}>APT | Air Purification Trading</h2>
          <p style={{ textAlign: 'center', marginBottom: '16px', color: '#6c757d' }}>
            Sign in to search and compare fans.
          </p>
          {loginError && (
            <div className="alert alert-danger" role="alert">
              {loginError}
            </div>
          )}
          <form onSubmit={handleLoginSubmit} style={{ display: 'grid', gap: '12px' }}>
            <div>
              <label className="form-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                name="email"
                className="form-control"
                value={loginForm.email}
                onChange={handleLoginChange}
                required
              />
            </div>
            <div>
              <label className="form-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                name="password"
                className="form-control"
                value={loginForm.password}
                onChange={handleLoginChange}
                required
                minLength={8}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loggingIn}>
              {loggingIn ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Layout: header + container + footer ---
  return (
    <div className="app-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar bg="light" expand="lg" className="shadow-sm">
        <Container fluid>
          <Navbar.Brand href="#" className="d-flex align-items-center">
            <img 
              src={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/logo.png`}
              alt="APT | Air Purification Trading logo"
              style={{ height: '60px', maxWidth: '100%', objectFit: 'contain' }}
              className="me-2"
            />
            <span className="fw-semibold" style={{ fontSize: '1rem', color: '#0d6efd' }}>
              APT | Air Purification Trading
            </span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="main-nav" />
          <Navbar.Collapse id="main-nav" className="justify-content-between">
            <Nav className="me-auto">
              <Nav.Link href="#search">Search</Nav.Link>
              <Nav.Link href="#charts">Charts</Nav.Link>
              <Nav.Link href="#print">Print</Nav.Link>
            </Nav>
            <div className="d-flex align-items-center gap-2 mt-3 mt-lg-0">
              <span className="navbar-text" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                Signed in as {user?.name || user?.email}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="btn btn-outline-secondary btn-sm"
              >
                Logout
              </button>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main style={{ flex: 1, padding: '15px 0' }}>
        <Container fluid style={{ maxWidth: 1400 }}>
          <Row>
            <Col xs={12} lg={3} className="border-lg-end pe-lg-4 mb-4 mb-lg-0 pb-4 pb-lg-0">
              <div className="mb-3">
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'input' ? 'active' : ''}`}
                      onClick={() => setActiveTab('input')}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      Input
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === 'preferences' ? 'active' : ''}`}
                      onClick={() => setActiveTab('preferences')}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      Preferences
                    </button>
                  </li>
                </ul>
              </div>

              {activeTab === 'input' && (
                <FanForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmit}
                  categories={categories}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  loading={loading}
                />
              )}

              {activeTab === 'preferences' && (
                <div className="mt-3">
                  <h6>Preferences</h6>
                  <div className="form-group">
                    <label>Air Temperature (°C)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="airTemperature"
                      value={formData.airTemperature}
                      onChange={(e) => {
                        const newTemp = toNumberSafe(e.target.value, 20);
                        const pressure = 101325; // Pa
                        const R = 287.058; // J/(kg·K)
                        const tempK = Number(newTemp) + 273.15; // Convert °C → K
                        const density = pressure / (R * tempK);
                        setFormData((prev) => ({
                          ...prev,
                          airTemperature: newTemp,
                          airDensity: density,
                        }));
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Air Density (kg/m³)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="airDensity"
                      value={formData.airDensity}
                      readOnly
                    />
                    <small className="form-text text-muted">
                      Auto-calculated from air temperature using ρ = p / (R × T)
                    </small>
                  </div>
                </div>
              )}
            </Col>

            <Col xs={12} lg={9}>
              <h5 id="search">Fan Selection List</h5>

              {loading ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{ minHeight: '220px', color: '#6b7280' }}
                >
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="sr-only"></span>
                  </div>
                  <div>Searching for matching fans…</div>
                </div>
              ) : (
                <>
                  {!hasMatches && apiMessage && (
                    <div className="alert alert-warning">{apiMessage}</div>
                  )}

                  <FansTable 
                    fans={fans} 
                    onSelectFan={handleFanSelection} 
                    selectedFan={selectedFan}
                    airFlowUnit={activeAirFlowUnit || 'm3h'}
                    pressureUnit={activeStaticPressureUnit || 'pa'}
                  />

                  <h5 className="mt-4" id="charts">Performance Chart {selectedFan ? `for ${selectedFan.model}` : ''}</h5>
                  <PerformanceCharts 
                    chartData={chartData} 
                    powerData={powerChartData} 
                    onChartClick={handleChartClick}
                    selectedFan={selectedFan}
                    certificates={certificates}
                    motorsForCategory={motorsForCategory}
                    selectedMotorForPrint={selectedMotorForPrint}
                    setSelectedMotorForPrint={setSelectedMotorForPrint}
                    airFlowUnit={activeAirFlowUnit || 'm3h'}
                    pressureUnit={activeStaticPressureUnit || 'pa'}
                    showPowerChart={selectedFan ? ['1', 1, true, 'true'].includes(selectedFan.show_power_curve) : true}
                  />

                  <div className="d-flex justify-content-end mb-3" id="print" style={{ marginTop: 12 }}>
                    {selectedFan ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowPrintModal(true)}
                      >
                        Preview &amp; Print
                      </button>
                    ) : (
                      <div className="text-muted align-self-center">Select a fan to preview/print</div>
                    )}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </main>

      <footer style={{ background: '#f8f9fa', padding: '12px 0' }}>
        <Container style={{ maxWidth: 1200, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13, color: '#666' }}>© {new Date().getFullYear()} APT | Air Purification Trading</div>
          <div style={{ fontSize: 13, color: '#666' }}>Developed By Coredex Solutions</div>
        </Container>
      </footer>

      <PrintPreviewModal
        show={showPrintModal}
        onHide={() => setShowPrintModal(false)}
        selectedFan={selectedFan}
        formData={{
          ...formData,
          // Use active units for PDF/print (only updated on search)
          airFlowUnit: activeAirFlowUnit,
          staticPressureUnit: activeStaticPressureUnit,
        }}
        nearestPoint={workingPoint}  // Use workingPoint (intersection) instead of nearestPoint for calculations
        motorsForCategory={motorsForCategory}
        selectedMotorForPrint={selectedMotorForPrint}
        setSelectedMotorForPrint={setSelectedMotorForPrint}
        chartData={chartData}              // pass the same chartData used on the main page
        powerData={powerChartData}         // pass the power chart data
        userPoint={userPoint}              // optional: pass user's input point to highlight in chart
        certificates={certificates}
        showPowerChart={selectedFan ? ['1', 1, true, 'true'].includes(selectedFan.show_power_curve) : true}
      />

      
    </div>
  );
}
